import './signup.css';

import EventEmitter from 'eventemitter3';
import { Modal } from '../modal'
import { ProfileService } from '../../../services/profile-service';
import { DataLayerService } from '../../../services/data-layer-service';
import { TwoWayBindingHelper } from '../../../utils/two-way-binding';
import { IProfile } from '../../../services/profile.interfaces';
import { Data } from '../../../models/data';
import { EventTypes } from '../../../core/event-bus';
import { BirthdayRequirement, EmailTextMarketingType, FieldType, LoyaltyProvider, Modals } from '../../../types/enums';
import { Util } from '../../../utils/util';
import { LoaderSpinner } from '../../shared/LoaderSpinner/loader-spinner';
import { IGoogleIdentity, ISignIn } from '../../../services/profile-service.interface';
import { GoogleSignInService } from '../../../services/google-signin-service';
import { requestNativeGoogleSignInAsync } from '../../../utils/native-bridge';
import { Context } from '../../../models/context';
import { LocalizationService } from '../../../services/localization-service';
import { DialogCreators } from '../../../utils/dialog-creators';
import { InputValidator } from '../../../validators/input-validator';
import { IValidationField } from '../../../validators/validation-field.interface';
import { StoreSelector } from '../StoreSelector/store-selector';
import { StoreSelectorUtil } from '../StoreSelector/store-selector-util';
import { IStoreLocation } from '../../../models/data.interface';

/**
 * Represents the Signup modal for user registration.
 */
export class Signup extends Modal {
    private readonly _data: Data;
    private readonly _profileService: ProfileService;
    private _item: IProfile;
    private _bindingHelper: TwoWayBindingHelper<IProfile>;

    private readonly _submit: HTMLElement | null;
    private readonly _next: HTMLElement | null;
    private readonly _start: HTMLElement | null;
    private readonly _full: HTMLElement | null;
    private readonly _login: HTMLElement | null;
    private readonly _labelTerms: HTMLElement | null;
    private readonly _checkboxTerms: HTMLInputElement | null;
    private readonly _password: HTMLInputElement | null;
    private readonly _confirm: HTMLInputElement | null;
    private readonly _defaultBirthYear = 2000;
    private readonly _changeStoreButton: HTMLElement | null = null;
    private readonly _storeSelectorModal: StoreSelector | null = null;

    private _shouldDisplayValidationMessages: boolean = false;

    private readonly _googleSignInService: GoogleSignInService;
    private readonly _googleButtonHost: HTMLElement | null;
    private _googleButtonRendered: boolean = false;
    /** Verified Google identity for this sign-up; makes the password optional. */
    private _isGoogleSignUp: boolean = false;
    /** Held to sign in via Google once the form creates the account. */
    private _googleIdToken: string | null = null;
    /** Native Android path only; sent with the sign-up submit and again with the sign-in that follows. */
    private _googleNonce: string | null = null;
    /** Handed over by the Login modal when the verified email has no profile. */
    private _pendingGoogleHandoff: { idToken: string, identity: IGoogleIdentity, nonce: string | null } | null = null;

    /**
     * Initializes a new instance of the Signup modal.
     * @param {EventEmitter} eventBus - The event bus for handling events.
     * @param {Data} data - The service managing application data.
     * @param {ProfileService} profileService - The service managing user profiles.
     */
    constructor(eventBus: EventEmitter, data: Data, context: Context, localizationService: LocalizationService, profileService: ProfileService, dataLayer: DataLayerService, storeSelectorModal: StoreSelector) {
        super("modal_signup", eventBus, context, dataLayer, localizationService, { hasCloseButton: true, ariaLabel: "Sign Up" }, Modals.SIGNUP);
        this._data = data;
        this._profileService = profileService;

        this._submit = document.getElementById("btn_signup_submit");
        this._next = document.getElementById("btn_signup_next")
        this._login = document.getElementById("btn_sign_up_login");

        this._start = document.getElementById("signup_start");
        this._full = document.getElementById("signup_full");

        this._labelTerms = document.getElementById("lbl_signup_terms");
        this._checkboxTerms = document.getElementById("chk_signup_terms") as HTMLInputElement;

        this._password = document.getElementById("txt_signup_password") as HTMLInputElement;
        this._confirm = document.getElementById("txt_signup_confirm") as HTMLInputElement;

        this._changeStoreButton = document.getElementById("btn_signup_default_store_change");
        this._storeSelectorModal = storeSelectorModal;

        this._googleButtonHost = document.getElementById("div_signup_google_button");
        this._googleSignInService = new GoogleSignInService(context.getProperty("googleOAuthClientId"));

        this._item = {} as IProfile;
        this._bindingHelper = new TwoWayBindingHelper(this._item);

        this._start && (this._start.style.display = "none");
        this._full && (this._full.style.display = "");

        this._setEventBusListener();
        //2DO: Incomplete User?
        //    this._incompleteUser = (aOLO.User && aOLO.User.IsProfileComplete == false) || false;
    }

    //2DO: Sign Up Link from POS
    //        if (aOLO.Temp.SignUpLoyalty) {
    //            this._item.IsLoyalty = true;
    //            delete aOLO.Temp.SignUpLoyalty;
    //        } else if (aOLO.SignUpLoyaltyLink) {
    //            this._item.Email = aOLO.SignUpLoyaltyLink.Email ?? this._item.Email;

    /**
     * Initializes the modal, setting up event listeners and data bindings.
     */
    public override init(): void {
        super.init();
        this._setEventListeners();
    }

    /**
     * Opens the modal and sets up necessary elements and bindings.
     */
    public override open(): void {
        super.open();
        Util.ensureAnnouncerPresent();
        this._item = this._profileService.profile;
        this._bindingHelper = new TwoWayBindingHelper(this._item);
        this._setTwoWayListeners();
        this._configureMarketingPreferences();
        this._populateBirthdaySelectLists();
        this._setPreferredStore();

        this._start && (this._start.style.display = "none");
        this._full && (this._full.style.display = "");

        this._configureEmailConfirmationField();
        this._clearErrorMessages();
        this._shouldDisplayValidationMessages = false;
        this._resetGoogleSignUpState();

        const loyaltyInfo = this._data.getLoyaltyInfo();
        const pointsUnit = this._localizationService.getLoyaltyUnitName(loyaltyInfo);
        const fancyTitleText = this._localizationService.names("SignInSlogan")
            .replaceAll("{{points_unit}}", pointsUnit);
        Util.setElement("innerHTML", "p_signup_fancy_title", fancyTitleText);

        // Already verified via Login, so apply it rather than offering the button again.
        const handoff = this._pendingGoogleHandoff;
        this._pendingGoogleHandoff = null;

        if (handoff?.identity?.email) {
            this._googleIdToken = handoff.idToken;
            this._googleNonce = handoff.nonce;
            this._prefillFromGoogle(handoff.identity);
            // Take the page-wide callback, or a credential now would run Login's handler.
            this._googleSignInService.claimCredentialHandler(
                (idToken: string) => void this._handleGoogleCredentialAsync(idToken));
            return;
        }

        this._setupGoogleSignInAsync();
    }

    /** Without this, a stale token or disabled email field carries into a manual sign-up. */
    private _resetGoogleSignUpState(): void {
        this._isGoogleSignUp = false;
        this._googleIdToken = null;
        this._googleNonce = null;

        const email = document.getElementById("txt_signup_email") as HTMLInputElement | null;
        if (email)
            email.disabled = false;

        document.querySelector('label[for="txt_signup_password"]')?.classList.add("required-star");
        document.querySelector('label[for="txt_signup_confirm"]')?.classList.add("required-star");
    }

    private async _setupGoogleSignInAsync(): Promise<void> {
        const isAvailable = this._profileService.loyaltyProvider.hasGoogleSignIn();

        if (isAvailable && Util.isAndroidAppView(this._context.getProperty("platform")) && typeof startNativeGoogleSignIn === "function") {
            Util.hideElement("div_signup_google_button");
            Util.showElement("div_signup_google");
            Util.showElement("btn_signup_google_native");
            Util.setElement("onclick", "btn_signup_google_native", this._handleNativeGoogleSignInAsync);
            return;
        }

        Util.hideElement("btn_signup_google_native");
        this._googleButtonRendered = await this._googleSignInService.mountButtonAsync({
            sectionId: "div_signup_google",
            host: this._googleButtonHost,
            alreadyRendered: this._googleButtonRendered,
            isAvailable: isAvailable,
            onCredential: (idToken: string) => void this._handleGoogleCredentialAsync(idToken),
            locale: this._context.getProperty("languageCode"),
            text: "signup_with"
        });
    }

    /** Google blocks its web sign-in inside the Android app's WebView, so use the native sheet instead. */
    private readonly _handleNativeGoogleSignInAsync = async (): Promise<void> => {
        // No nonce means the server would reject the Android token after the user picked an account,
        // so fail before opening the sheet. A network error rejects rather than returning null.
        const nonce = await this._profileService.getGoogleSignInNonce().catch(() => null);
        const result = nonce
            ? await requestNativeGoogleSignInAsync({
                nonce,
                serverClientId: this._context.getProperty("googleOAuthClientId")
            })
            : null;

        if (!result?.idToken) {
            DialogCreators.messageBoxOk(
                this._localizationService,
                this._dataLayer,
                this._localizationService.names("GoogleSignInFailed"),
                true
            );
            return;
        }

        await this._handleGoogleCredentialAsync(result.idToken, nonce ?? undefined);
    }

    /**
     * Existing customer signs straight in; a new one keeps the form open, prefilled.
     */
    private async _handleGoogleCredentialAsync(idToken: string, nonce?: string): Promise<void> {
        const loader = new LoaderSpinner();
        const result = await this._profileService.signInGoogle(idToken, true, nonce);
        loader?.destroy();

        if (!result.success) {
            DialogCreators.messageBoxOk(
                this._localizationService,
                this._dataLayer,
                result.message ?? this._localizationService.names("GoogleSignInFailed"),
                true
            );
            return;
        }

        if (result.isNewProfile) {
            // Nothing created server-side; this form's submit creates the account.
            this._googleIdToken = idToken;
            this._googleNonce = nonce ?? null;
            this._prefillFromGoogle(result.googleIdentity);
            return;
        }

        this.close();
        this._eventBus.emit(EventTypes.LOGGED_IN);
    }

    /**
     * Prefills the Create Profile fields from the Google-verified identity and locks the email
     * field. Serverside we get email address from token so no one can spoof the email. 
     */
    private _prefillFromGoogle(identity?: IGoogleIdentity): void {
        // Guard on the email: a response missing Identity still arrives as a blank object.
        if (!identity?.email)
            return;

        this._isGoogleSignUp = true;

        this._item.Email = identity.email || this._item.Email;
        this._item.Name = identity.firstName || this._item.Name;
        this._item.LastName = identity.lastName || this._item.LastName;

        // Drop the previous bindings before rebinding, or every input ends up double-bound.
        this._bindingHelper.clearListeners();
        this._bindingHelper = new TwoWayBindingHelper(this._item);
        this._setTwoWayListeners();

        const email = document.getElementById("txt_signup_email") as HTMLInputElement | null;
        if (email) {
            email.value = this._item.Email ?? "";
            email.disabled = true;
        }

        Util.setElement("value", "txt_signup_name", this._item.Name ?? "");
        Util.setElement("value", "txt_signup_lname", this._item.LastName ?? "");

        this._setPasswordOptionalForGoogle();

        // UI#4
        this._highlightMissingGoogleFields();

        Util.hideElement("div_signup_google");
    }

    private _setPasswordOptionalForGoogle(): void {
        document.querySelector('label[for="txt_signup_password"]')?.classList.remove("required-star");
        document.querySelector('label[for="txt_signup_confirm"]')?.classList.remove("required-star");
    }

    /** Flags the required fields Google didn't fill, before the customer submits. */
    private _highlightMissingGoogleFields(): void {
        const requiredInputIds = ["txt_signup_name", "txt_signup_lname", "txt_signup_phone", "txt_signup_email"];

        if (this._data.getBrandProperty("Settings")?.OOS?.showEmailConfirmationField)
            requiredInputIds.push("txt_signup_confirm_email");

        for (const inputId of requiredInputIds) {
            const input = document.getElementById(inputId) as HTMLInputElement | null;
            if (input && !input.value.trim())
                input.classList.add("invalid-input");
        }
    }

    /**
     * Attaches event listeners to modal elements.
     */
    protected _setEventListeners(): void {
        this._submit?.addEventListener("click", this._submitSignupAsync);

        //this._next?.addEventListener("click", async () => {
        //    const valid = await Util.IsValidEmail(this._item.Email);
        //    if (!valid) {
        //        document.getElementById("spn_signup_email_start_error")?.classList.toggle("hidden")
        //        document.getElementById("txt_signup_email_start")?.classList.toggle("invalid-input");
        //    } else {
        //        this._start && (this._start.style.display = "none");
        //        this._full && (this._full.style.display = "")
        //    }
        //});

        this._login?.addEventListener("click", () => {
            this.close();
            this._eventBus.emit(EventTypes.MODAL_OPEN, Modals.LOGIN);
        });

        this._setupPasswordToggle(this._password, "txt_signup_password_show", "txt_signup_password_hide");
        this._setupPasswordToggle(this._confirm, "txt_signup_confirm_show", "txt_signup_confirm_hide");

        this._setInputValidationEvents("txt_signup_name", ["spn_signup_name_error"], FieldType.NAME);
        this._setInputValidationEvents("txt_signup_lname", ["spn_signup_lname_error"], FieldType.NAME);

        this._setInputValidationEvents(
            "txt_signup_phone",
            ["spn_signup_phone_error", "spn_signup_phone_same_error"],
            FieldType.PHONE,
            [],
            ["select_signup_phone_country"]
        );

        this._setInputValidationEvents(
            "txt_signup_email",
            ["spn_signup_email_error", "spn_signup_email_same_error"],
            FieldType.EMAIL
        );

        this._setInputValidationEvents("txt_signup_password", ["spn_signup_password_error"], FieldType.PASSWORD);

        this._setInputValidationEvents("txt_signup_confirm", ["spn_signup_confirm_error"], FieldType.COMPAREPASSWORDS, [
            this._item.pwd,
            this._item.repeatPwd,
        ]);

        this._setInputValidationEvents("chk_signup_terms", ["spn_signup_terms_error"], FieldType.CHECKED);

        this._changeStoreButton?.addEventListener("click", this.onChangeStore);
    }

    private _setInputValidationEvents(inputId: string, errorIds: string[], fieldType: FieldType, extraData?: any[], extraIds?: string[]): void {
        const inputElement = document.getElementById(inputId) as HTMLInputElement;

        if (inputElement) {
            inputElement.oninput = () => {
                errorIds.forEach(errorId => {
                    Util.hideElementErrors(inputId, errorId);
                });
            };

            inputElement.onblur = () => {
                if (this._shouldDisplayValidationMessages) {
                    InputValidator.validate(fieldType, inputId, errorIds[0], true, true, extraData, extraIds, this._localizationService, this._dataLayer);
                }
            };
        }
    }

    /**
     * Sets up event listeners for password visibility toggles.
     * @param {HTMLInputElement | null} input - The input of the password input field.
     * @param {string} showId - The ID of the show password toggle.
     * @param {string} hideId - The ID of the hide password toggle.
     */
    private _setupPasswordToggle(input: HTMLInputElement | null, showId: string, hideId: string): void {
        const showToggle = document.getElementById(showId);
        const hideToggle = document.getElementById(hideId);

        showToggle?.addEventListener("click", () => {
            if (input) input.type = "text";
            if (showToggle) showToggle.style.display = "none";
            if (hideToggle) hideToggle.style.display = "";
        });

        hideToggle?.addEventListener("click", () => {
            if (input) input.type = "password";
            if (showToggle) showToggle.style.display = "";
            if (hideToggle) hideToggle.style.display = "none";
        });

        hideToggle?.click(); // Default to hiding the password
    }

    private _setPreferredStore(): void {
        const nearestStore = this._getNearestStore();
        if (nearestStore) {
            this._item.DefaultStoreId = nearestStore.storeId;
            this._renderSelectedStore(nearestStore);
        }
    }

    private _getNearestStore(): IStoreLocation | null {
        const stores = this._data?.locations || [];
        if (stores.length === 0 || !stores[0] || stores[0].distance == 0) return null;

        // stores are assumed to be sorted by distance, with the nearest store first
        return stores[0];
    }

    private readonly onChangeStore = async (): Promise<void> => {
        const selectedStore = await this._storeSelectorModal?.openDialog(); // suspends here until dialog closes

        if (selectedStore) {
            this._item.DefaultStoreId = selectedStore.storeId;
            this._renderSelectedStore(selectedStore);
            Util.hideElementErrors("hid_signup_store_selection", "spn_signup_store_selection_error");
            Util.hideElementErrors("btn_signup_default_store_change", "spn_signup_store_selection_error");
        }
    }

    private _renderSelectedStore(selectedStore: IStoreLocation): void {
        const container = document.getElementById("div_signup_default_store_card_container");
        if (container) {
            const storeCard = StoreSelectorUtil.createStoreCardElement(selectedStore);
            container.innerHTML = "";

            if (storeCard) container.appendChild(storeCard);
        }
    }

    /**
    * Sets up event listeners for brand-related settings retrieval.
    */
    protected _setEventBusListener(): void {
        this._eventBus.on(EventTypes.BRAND_DATA_RETRIEVED, this._handleOnBrandDataRetrieved);

        // The Login modal hands off here when Google verified an email that has no profile yet.
        // Stashed rather than applied directly: this fires before open(), whose reset would
        // otherwise wipe it.
        this._eventBus.on(EventTypes.MODAL_OPEN, (modal: Modals, options?: { googleIdToken?: string, googleIdentity?: IGoogleIdentity, googleNonce?: string }) => {
            if (modal !== Modals.SIGNUP)
                return;

            this._pendingGoogleHandoff = options?.googleIdToken && options?.googleIdentity
                ? { idToken: options.googleIdToken, identity: options.googleIdentity, nonce: options.googleNonce ?? null }
                : null;
        });
    }

    /**
     * Handles initialization logic after brand-specific data has been retrieved.
     * 
     * Populates the country code select list and configures marketing preferences
     * and birthday visibility settings based on brand configuration.
     */
    private readonly _handleOnBrandDataRetrieved = (): void => {
        Util.populateCountryCodeSelectList(
            "select_signup_phone_country",
            "txt_signup_phone",
            this._data.getBrandProperty("Countries"),
            this._data.getBrandProperty("StoreInfo").countryId || 1
        );

        this._configureMarketingPreferences();
        this._configureBirthdaySetting();
        this._configureEmailConfirmationField();
    }

    /**
     * Configures marketing preference visibility based on brand settings.
     */
    private _configureMarketingPreferences(): void {
        const marketingEmail = document.getElementById("signup_marketing_email");
        const marketingText = document.getElementById("signup_marketing_text");
        const marketingSurvey = document.getElementById("signup_marketing_survey");

        switch (this._data.getBrandProperty("Settings").OOS.marketingOption) {
            case EmailTextMarketingType.NONE:
                if (marketingEmail)
                    marketingEmail.style.display = "none";
                if (marketingText)
                    marketingText.style.display = "none";

                this._item.IsMarketingEmail = false;
                this._item.IsMarketingText = false;
                break;
            case EmailTextMarketingType.TEXT_ONLY:
                if (marketingEmail)
                    marketingEmail.style.display = "none";

                this._item.IsMarketingEmail = false;
                this._item.IsMarketingText = true;
                break;
            case EmailTextMarketingType.EMAIL_ONLY:
                if (marketingText)
                    marketingText.style.display = "none";

                this._item.IsMarketingText = false;
                this._item.IsMarketingEmail = true;
                break;
            case EmailTextMarketingType.EMAIL_AND_TEXT:
                this._item.IsMarketingEmail = true;
                this._item.IsMarketingText = true;
                break;
        }

        if (this._data.getBrandProperty("Settings").OOS.showSurveyOption) {
            this._item.IsMarketingSurvey = true;
        }
        else {
            if (marketingSurvey)
                marketingSurvey.style.display = "none";

            this._item.IsMarketingSurvey = false;
        }
    }

    /**
     * Configures visibility of the birthday input section on the signup form
     * based on the birthday requirement setting in brand configuration.
     */
    private _configureBirthdaySetting(): void {
        const birthdayRequirement = this._data.getBrandProperty("Settings").OOS.birthdayField;
        const labelSignUpBirthday = document.getElementById("lbl_signup_birthday");

        switch (birthdayRequirement) {
            case BirthdayRequirement.DO_NOT_SHOW:
                Util.hideElement("div_signup_birthday");
                break;
            case BirthdayRequirement.OPTIONAL:
                Util.showElement("div_signup_birthday");
                Util.showElement("span_signup_birthday_optional");
                if (labelSignUpBirthday)
                    labelSignUpBirthday.classList.remove("required-star");
                break;
            case BirthdayRequirement.REQUIRED:
                Util.showElement("div_signup_birthday");
                Util.hideElement("span_signup_birthday_optional");
                if (labelSignUpBirthday)
                    labelSignUpBirthday.classList.add("required-star");
                break;
        }

        if (birthdayRequirement == BirthdayRequirement.REQUIRED) {
            this._setInputValidationEvents("select_signup_birthday_month", ["spn_signup_birthday_month_error"], FieldType.CUSTOM, [this._birthdaySelectValidator]);
            this._setInputValidationEvents("select_signup_birthday_day", ["spn_signup_birthday_day_error"], FieldType.CUSTOM, [this._birthdaySelectValidator]);
        }
    }

    private _configureEmailConfirmationField(): void {
        if (this._data.getBrandProperty("Settings")?.OOS?.showEmailConfirmationField) {
            Util.showElement("div_signup_confirm_email");
            this._setInputValidationEvents(
                "txt_signup_confirm_email",
                ["spn_signup_confirm_email_error"],
                FieldType.CUSTOM,
                [(confirmValue: string) => confirmValue.trim() === (this._item.Email?.trim() ?? "")]
            );

            const confirmEmailInput = document.getElementById("txt_signup_confirm_email") as HTMLInputElement | null;
            if (confirmEmailInput) {
                let justPasted = false;
                confirmEmailInput.onpaste = () => {
                    justPasted = true;
                    Util.showElement("spn_signup_confirm_email_paste_warning");
                };

                const hideErrorsOnInput = confirmEmailInput.oninput;
                confirmEmailInput.oninput = (event) => {
                    // The paste's own input event must not immediately hide the warning it just showed.
                    if (justPasted) {
                        justPasted = false;
                    } else {
                        Util.hideElement("spn_signup_confirm_email_paste_warning");
                    }
                    hideErrorsOnInput?.call(confirmEmailInput, event);
                };
            }
        } else {
            Util.hideElement("div_signup_confirm_email");
        }
    }

    private readonly _birthdaySelectValidator = (value: string): boolean => {
        const numericValue = Number.parseInt(value);
        return !Number.isNaN(numericValue) && numericValue !== -1;
    }

    /**
     * Initializes two-way data binding for form inputs.
     */
    private _setTwoWayListeners(): void {
        this._bindingHelper.bindToInput("Name", "txt_signup_name");
        this._bindingHelper.bindToInput("LastName", "txt_signup_lname");
        this._bindingHelper.bindToInput("Phone", "txt_signup_phone");
        this._bindingHelper.bindToInput("MarketingPhone", "txt_signup_phone");
        this._bindingHelper.bindToInput("CountryCodeId", "select_signup_phone_country");
        this._bindingHelper.bindToInput("MarketingCountryCodeId", "select_signup_phone_country");
        this._bindingHelper.bindToInput("Email", "txt_signup_email_start");
        this._bindingHelper.bindToInput("Email", "txt_signup_email");
        this._bindingHelper.bindToInput("pwd", "txt_signup_password");
        this._bindingHelper.bindToInput("repeatPwd", "txt_signup_confirm");
        this._bindingHelper.bindToInput("BirthMonth", "select_signup_birthday_month");
        this._bindingHelper.bindToInput("BirthDay", "select_signup_birthday_day");
        this._bindingHelper.bindToInput("TermsPrivacyPolicy", "chk_signup_terms");
        this._bindingHelper.bindToInput("IsMarketingEmail", "chk_signup_marketing_email");
        this._bindingHelper.bindToInput("IsMarketingText", "chk_signup_marketing_text");
        this._bindingHelper.bindToInput("IsMarketingSurvey", "chk_signup_marketing_survey");
        this._bindingHelper.bindToInput("DefaultStoreId", "hid_signup_store_selection");
    }

    /**
     * Clears all error messages from the form fields.
     */
    private _clearErrorMessages(): void {
        const errorFields = [
            { input: "txt_signup_name", error: "spn_signup_name_error" },
            { input: "txt_signup_lname", error: "spn_signup_lname_error" },
            { input: "txt_signup_email", error: "spn_signup_email_error" },
            { input: "txt_signup_email", error: "spn_signup_email_same_error" },
            { input: "txt_signup_phone", error: "spn_signup_phone_error" },
            { input: "txt_signup_phone", error: "spn_signup_phone_same_error" },
            { input: "select_signup_phone_country", error: "" },
            { input: "txt_signup_password", error: "spn_signup_password_error" },
            { input: "txt_signup_confirm", error: "spn_signup_confirm_error" },
            { input: "txt_signup_confirm_email", error: "spn_signup_confirm_email_error" },
            { input: "select_signup_birthday_month", error: "spn_signup_birthday_error" },
            { input: "select_signup_birthday_day", error: "spn_signup_birthday_error" },
            { input: "select_signup_birthday_month", error: "spn_signup_birthday_month_error" },
            { input: "select_signup_birthday_day", error: "spn_signup_birthday_day_error" },
        ];

        errorFields.forEach(field => Util.hideElementErrors(field.input, field.error));
        document.getElementById("spn_signup_terms_error")?.classList.toggle("hidden", true);
    }

    /**
     * Validates the submitted signup form data.
     * Ensures all required fields are filled correctly and checks for duplicate accounts.
     *
     * @returns {Promise<boolean>} - A promise resolving to `true` if validation passes, otherwise `false`.
     */
    private async _validateSubmittedDataAsync(): Promise<boolean> {
        this._shouldDisplayValidationMessages = true;
        this._clearErrorMessages();

        const validationFields: IValidationField[] = [
            { type: FieldType.NAME, inputId: "txt_signup_name", errorId: "spn_signup_name_error" },
            { type: FieldType.NAME, inputId: "txt_signup_lname", errorId: "spn_signup_lname_error" },
            {
                type: FieldType.PHONE,
                inputId: "txt_signup_phone",
                errorId: "spn_signup_phone_error",
                extraIds: ["select_signup_phone_country"],
            },
            { type: FieldType.EMAIL, inputId: "txt_signup_email", errorId: "spn_signup_email_error" },
            { type: FieldType.CHECKED, inputId: "chk_signup_terms", errorId: "spn_signup_terms_error" },
            {
                type: FieldType.CUSTOM,
                inputId: "hid_signup_store_selection",
                errorId: "spn_signup_store_selection_error",
                extraData: [(value: string) => Number(value) > 0],
                extraIds: ["btn_signup_default_store_change"],
            },
        ];

        // Optional for a Google sign-up, unless the customer typed one anyway.
        if (!this._isGoogleSignUp || this._item.pwd || this._item.repeatPwd) {
            validationFields.push(
                {
                    type: FieldType.PASSWORD,
                    inputId: "txt_signup_password",
                    errorId: "spn_signup_password_error",
                },
                {
                    type: FieldType.COMPAREPASSWORDS,
                    inputId: "txt_signup_confirm",
                    errorId: "spn_signup_confirm_error",
                    extraData: [this._item.pwd, this._item.repeatPwd],
                });
        }

        if (this._data.getBrandProperty("Settings")?.OOS?.showEmailConfirmationField) {
            validationFields.push({
                type: FieldType.CUSTOM,
                inputId: "txt_signup_confirm_email",
                errorId: "spn_signup_confirm_email_error",
                extraData: [(confirmValue: string) => confirmValue.trim() === (this._item.Email?.trim() ?? "")],
            });
        }

        const birthdayRequirement = this._data.getBrandProperty("Settings").OOS.birthdayField;

        if (birthdayRequirement === BirthdayRequirement.REQUIRED) {
            validationFields.push(
                {
                    type: FieldType.CUSTOM,
                    inputId: "select_signup_birthday_month",
                    errorId: "spn_signup_birthday_month_error",
                    extraData: [this._birthdaySelectValidator],
                },
                {
                    type: FieldType.CUSTOM,
                    inputId: "select_signup_birthday_day",
                    errorId: "spn_signup_birthday_day_error",
                    extraData: [this._birthdaySelectValidator],
                },
            );
        }

        if (birthdayRequirement !== BirthdayRequirement.DO_NOT_SHOW && !this._isBirthdayValid()) {
            validationFields.push({ type: FieldType.FAILED, inputId: "select_signup_birthday_month", errorId: "spn_signup_birthday_error", extraIds: ["select_signup_birthday_day"] });
        }

        // Validate all fields and check if any failed
        const hasErrors = !InputValidator.validateFields(validationFields, true, true, this._localizationService, this._dataLayer);

        if (hasErrors)
            return false;

        // Check if the email or phone number is already associated with an account
        let profileExists = 0;
        if (this._profileService.loyaltyProvider.getLoyaltyProvider() != LoyaltyProvider.PUNCHH) {
            const loader = new LoaderSpinner();
            profileExists = await this._profileService.checkIfLoggedInExists(this._item.ProfileId, this._item.Email || "", Util.cleanNonDigits(this._item.Phone || ""), this._item.CountryCodeId || 1);
            loader?.destroy();
        }

        const errorCases: Record<string, IValidationField[]> = {
            "-2": [{ type: FieldType.FAILED, inputId: "txt_signup_phone", errorId: "spn_signup_phone_same_error", extraIds: ["select_signup_phone_country"] }], 
            "-1": [{ type: FieldType.FAILED, inputId: "txt_signup_email", errorId: "spn_signup_email_same_error" }],
            "-3": [
                { type: FieldType.FAILED, inputId: "txt_signup_phone", errorId: "spn_signup_phone_same_error", extraIds: ["select_signup_phone_country"] },
                { type: FieldType.FAILED, inputId: "txt_signup_email", errorId: "spn_signup_email_same_error" }                
            ]
        };

        if (profileExists in errorCases) {
            return !errorCases[profileExists.toString() as keyof typeof errorCases].map(field =>
                InputValidator.validate(field.type, field.inputId, field.errorId, true, true, undefined, field.extraIds,
                    this._localizationService, this._dataLayer)
            ).some(result => result);
        }

        return true;
    }

    /**
     * Handles the signup form submission.
     * Validates input, creates the account, and attempts automatic login.
     */
    private readonly _submitSignupAsync = async () => {

        Util.resetAnnounceCycle();
        Util.beginBatchAnnouncement();
        const isValid = await this._validateSubmittedDataAsync();
        Util.endBatchAnnouncement();

        if (!isValid) {
            Util.scrollToFirstInvalidElement("modal_signup");

            const errors = Util.collectVisibleErrors("modal_signup");
            Util.announceErrors(errors);

            return;
        }

        // Clean phone numbers of non-digit characters
        this._item.Name = this._item.Name.trim();
        this._item.LastName = this._item.LastName.trim();
        this._item.Phone = Util.cleanNonDigits(this._item.Phone);
        this._item.MarketingPhone = Util.cleanNonDigits(this._item.MarketingPhone);
        const birthdayRequirement = this._data.getBrandProperty("Settings").OOS.birthdayField;

        if (birthdayRequirement !== BirthdayRequirement.DO_NOT_SHOW) {
            this._item.BirthDate = this.getBirthDate();
        }

        const loader = new LoaderSpinner();
        const success = await this._profileService.signUp(this._item, this._googleIdToken, this._googleNonce);
        loader?.destroy();

        if (success) {
            // A Google sign-up may have no password, so sign in with the credential instead.
            let result: boolean;
            if (this._isGoogleSignUp && this._googleIdToken) {
                const googleResult = await this._profileService.signInGoogle(this._googleIdToken, true, this._googleNonce ?? undefined);
                result = googleResult.success && !googleResult.isNewProfile;
            } else {
                const signIn: ISignIn = {
                    email: this._item.Email,
                    password: this._item.pwd,
                    rememberMe: true
                }

                // Attempt to automatically sign in the user
                result = await this._profileService.signIn(signIn);
            }

            this.close();

            // The credential has served its purpose; don't keep it around after the account exists.
            this._googleIdToken = null;
            this._googleNonce = null;

            if (result) {
                this._eventBus.emit(EventTypes.LOGGED_IN);
            }
            else {
                DialogCreators.messageBoxOk(
                    this._localizationService,
                    this._dataLayer,
                    this._localizationService.names("UnableAutoSignIn"),
                    true,
                    () => this._eventBus.emit(EventTypes.MODAL_OPEN, Modals.LOGIN),
                );
            }

            // Send event to data layer
            if (this._dataLayer)
                this._dataLayer.sign_up(this._profileService.user.user, result);
        }
        else {
            // Show error message if signup fails
            DialogCreators.messageBoxOk(
                this._localizationService,
                this._dataLayer,
                this._localizationService.names("UnableCreateAccount"),
                true
            );
        }
    }

    private _populateBirthdaySelectLists(): void {
        const currentLanguage = this._context.getProperty("languageCode") || "en-US";
        const year = this._defaultBirthYear;
        this._item.BirthMonth = -1;
        this._item.BirthDay = -1;

        this._populateBirthdayMonthSelectList(year, currentLanguage);
        this._populateBirthdayDaySelectList(year, -1); // Default to not-selected value
    }

    private _populateBirthdayMonthSelectList(year: number, language: string): void {
        const monthSelect = document.getElementById("select_signup_birthday_month") as HTMLSelectElement;

        Util.populateMonthSelectList(monthSelect, year, language, this._localizationService);

        monthSelect.addEventListener("change", (event) => {
            const selectedMonth = Number.parseInt((event.target as HTMLSelectElement).value);
            this._populateBirthdayDaySelectList(year, selectedMonth);
            this._item.BirthDay = -1; // Reset day to not-selected value when month changes to avoid invalid dates
        });
    }

    private _populateBirthdayDaySelectList(year: number,month: number): void {
        const daySelect = document.getElementById("select_signup_birthday_day") as HTMLSelectElement;
        Util.populateDaySelectList(daySelect, year, month, this._localizationService);
    }

    private _isBirthdayValid(): boolean {
        if (!this._isBirthdaySet()) {
            return true; // as a valid not-selected state
        }
        return Util.isValidDate(this._defaultBirthYear, this._item.BirthMonth, this._item.BirthDay);
    }

    private getBirthDate(): Date | null {
        if (!this._isBirthdaySet() || !Util.isValidDate(this._defaultBirthYear, this._item.BirthMonth, this._item.BirthDay)) {
            return null;
        }
        return new Date(Date.UTC(this._defaultBirthYear, this._item.BirthMonth - 1, this._item.BirthDay));
    }

    private _isBirthdaySet(): boolean {
        return (this._item.BirthMonth != -1 && this._item.BirthDay != -1);
    }
}